/**
 * Pure calculation layer for the community earnings model.
 *
 * There is deliberately no API call here. Every number a prospective partner
 * sees is derived client-side from `commons/earnings.constants`, so the public
 * calculator, the landing page and the signup rail can never disagree with each
 * other. When a live earnings endpoint exists, it should return the same shape
 * `calculateEarnings` returns so consuming components need no changes.
 */

import {
  EARNINGS_ASSUMPTIONS,
  EARNING_TIERS,
  BONUS_THRESHOLD_PCT,
} from "commons/earnings.constants";

/**
 * Net platform fee retained on a single transaction, after GST.
 * @returns {number} rupees
 */
export function getNetFeePerTransaction() {
  return EARNINGS_ASSUMPTIONS.netFeePerTransaction;
}

/**
 * Resolve the earning tier a community sits in.
 * @param {number} members
 * @returns {typeof EARNING_TIERS[number]}
 */
export function getTierForMembers(members) {
  const safeMembers = Number.isFinite(members) ? members : 0;
  // Walk from the largest tier down so the open-ended top tier matches first.
  for (let i = EARNING_TIERS.length - 1; i >= 0; i -= 1) {
    if (safeMembers >= EARNING_TIERS[i].minMembers) return EARNING_TIERS[i];
  }
  return EARNING_TIERS[0];
}

/**
 * Estimate what a community earns in a month.
 *
 * @param {object} input
 * @param {number} input.members - members the community has on ShoUp
 * @param {number} input.bookingRatePct - share of those members who book in a month
 * @returns {{
 *   tier: object,
 *   transactingMembers: number,
 *   transactions: number,
 *   perTransaction: number,
 *   feeShare: number,
 *   bonus: number,
 *   bonusUnlocked: boolean,
 *   monthly: number,
 *   annual: number,
 * }}
 */
export function calculateEarnings({ members, bookingRatePct }) {
  const { transactionsPerActiveMember } = EARNINGS_ASSUMPTIONS;
  const netFee = getNetFeePerTransaction();
  const tier = getTierForMembers(members);

  const transactingMembers = Math.round((members * bookingRatePct) / 100);
  const transactions = Math.round(
    transactingMembers * transactionsPerActiveMember,
  );
  const feeShare = transactions * netFee * tier.share;

  const bonusUnlocked = bookingRatePct >= BONUS_THRESHOLD_PCT;
  let bonus = 0;
  if (bonusUnlocked) {
    bonus = tier.benchmark
      ? Math.min(tier.bonusMax, (tier.bonusMax * members) / tier.benchmark)
      : tier.bonusMax;
  }

  const monthly = feeShare + bonus;

  return {
    tier,
    transactingMembers,
    transactions,
    /** What the community keeps on each transaction, in rupees. */
    perTransaction: netFee * tier.share,
    feeShare,
    bonus,
    bonusUnlocked,
    monthly,
    annual: monthly * 12,
  };
}

/**
 * Format rupees in the Indian numbering system, e.g. ₹1,15,424.
 * @param {number} value
 * @returns {string}
 */
export function formatInr(value) {
  const rounded = Math.round(Number.isFinite(value) ? value : 0);
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(rounded)}`;
}

/**
 * Format large rupee amounts the way Indian partners read them, e.g. "₹13.8 lacs".
 * Falls back to the full number below a lac.
 * @param {number} value
 * @returns {string}
 */
export function formatInrCompact(value) {
  const amount = Number.isFinite(value) ? value : 0;
  // Trim a trailing ".0" so round numbers read "₹3 lacs", not "₹3.0 lacs".
  const trim = (n) => String(n).replace(/\.0+$/, "");
  if (amount >= 10000000) return `₹${trim((amount / 10000000).toFixed(2))} cr`;
  if (amount >= 100000) return `₹${trim((amount / 100000).toFixed(1))} lacs`;
  return formatInr(amount);
}

/**
 * Format a plain count in the Indian numbering system, e.g. 1,20,000.
 * @param {number} value
 * @returns {string}
 */
export function formatCount(value) {
  return new Intl.NumberFormat("en-IN").format(
    Math.round(Number.isFinite(value) ? value : 0),
  );
}
